import React, { useEffect, useState } from 'react';
import { FullPageLoader } from '../components/PageWrapper';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, Select, TextField, Typography, Link, FormControl, InputLabel } from '@mui/material';
import useAuth, { useAuthEffects } from '../components/Auth';
import { Trans, useTranslation } from 'react-i18next';
import { useGetAllShippingRegionsQuery } from '../services/shippingRegions';
import { useShippingRegionContext } from '../contexts/ShippingRegionContext';
import { useActivateCreatorProfileMutation } from '../services/accounts';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import slugify from 'slugify';
import { SetupAccountLayout } from './SetupAccountFlow';
import { useAppSelector } from '../hooks';
import { selectMyCompany } from './accountSlice';

const DEFAULT_PROFILE_ID = 'my-creative-page';

const SetupAccountCreator = () => {
	useAuthEffects();
	const {userProfile, logout, isAuthenticated, isAuthLoading} = useAuth();
	const {t} = useTranslation();
	const navigate = useNavigate();
	const {data: allShippingRegions} = useGetAllShippingRegionsQuery();
	const {selectedRegion, detectedRegion} = useShippingRegionContext();
	const [activateCreatorProfileMutation, {isSuccess: isActivateCreatorProfileSuccess, error: activateCreatorProfileError}] = useActivateCreatorProfileMutation();
	const [profileId, setProfileId] = useState(DEFAULT_PROFILE_ID);
	const [errorMessage, setErrorMessage] = useState('');
	const myCompany = useAppSelector(selectMyCompany);

	const supportedCountryCodes = ['US', 'EE'];
	const selectedRegionCode = selectedRegion ?? null;
	const detectedCountryCode = detectedRegion?.countryCode ?? null;

	const supportedRegionOptions = supportedCountryCodes.map((code) => {
		const apiRegion = allShippingRegions?.find((region) => region?.code === code);
		if (apiRegion) return { code: apiRegion.code, name: apiRegion.name };
		if (code === 'US') return { code, name: 'United States' };
		if (code === 'EE') return { code, name: 'Estonia' };
		return { code, name: code };
	});

	const extraRegionOption = selectedRegionCode && !supportedCountryCodes.includes(selectedRegionCode)
		? (() => {
			const apiRegion = allShippingRegions?.find((region) => region?.code === selectedRegionCode);
			const isDetected = Boolean(detectedRegion && detectedCountryCode === selectedRegionCode);
			if (isDetected) {
				const citySuffix = detectedRegion?.city ? ` (${detectedRegion.city})` : '';
				return {
					code: selectedRegionCode,
					name: `${detectedRegion?.countryName ?? selectedRegionCode}${citySuffix}`,
				};
			}
			if (apiRegion) return { code: apiRegion.code, name: apiRegion.name };
			return { code: selectedRegionCode, name: selectedRegionCode };
		})()
		: null;

	const regionOptions = extraRegionOption
		? [...supportedRegionOptions, extraRegionOption]
		: supportedRegionOptions;

	const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setProfileId(slugify(e.target.value, {lower: true, strict: true, locale: 'en', trim: true}) || DEFAULT_PROFILE_ID);
	};

	useEffect(() => {
		if (myCompany?.name) {
			setProfileId(slugify(myCompany.name, {lower: true, strict: true, locale: 'en', trim: true}));
		}
	}, [myCompany]);

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		const companyName = formData.get('companyName') as string;
		const region = formData.get('region') as string;
		if (formData) {
			// TODO: capture region
			activateCreatorProfileMutation({
				region: region,
				profileId: profileId,
				name: companyName,
			}).unwrap().then(() => {
				navigate('/profile');
			}).catch((error) => {
				const errorMessage = (error as any)?.data?.message;
				setErrorMessage(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
			});
		}
	};

	useEffect(() => {
		if (!isAuthenticated && !isAuthLoading) {
			navigate('/');
		}
	}, [isAuthenticated, isAuthLoading]);

	useEffect(() => {
		if (isActivateCreatorProfileSuccess) {
			navigate('/');
		}
	}, [isActivateCreatorProfileSuccess]);

	useEffect(() => {
		if (activateCreatorProfileError) {
			const errorMessage = (activateCreatorProfileError as any).data.message;
			setErrorMessage(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
		}
	}, [activateCreatorProfileError]);

	if (!userProfile) {
		return <FullPageLoader loadingMessage='Loading user profile...' />;
	}

	return <SetupAccountLayout><Box sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
		<Typography variant='body1' textAlign='center' py={2}>
			{t('signUpCreator.description')}
		</Typography>
		<form onSubmit={handleSubmit}>
			<Box display='flex' flexDirection='column' gap={2}>
				<TextField fullWidth autoComplete='off' label={t('signUpCreator.email')} name='email' value={userProfile.email} disabled />
				<TextField fullWidth required autoFocus autoComplete='off' label={t('signUpCreator.profileName')} name='companyName' onChange={handleCompanyNameChange} placeholder='My Creative Page' defaultValue={myCompany?.name} />
				<Typography variant='body2' textAlign='center' pb={2}>
					https://www.diyfeed.com/author/<span style={{fontWeight: 600}}>{profileId}</span>
				</Typography>
				<FormControl>
					<InputLabel id='region-label'>{t('signUpCreator.region')}</InputLabel>
					<Select fullWidth required autoComplete='off' name='region' label={t('signUpCreator.region')} defaultValue={selectedRegion}>
						<MenuItem value="" disabled>
							{t('signUpCreator.selectRegion')}
						</MenuItem>
						{regionOptions.map((region) => (
							<MenuItem key={region?.code} value={region?.code}>
								{region?.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControlLabel control={<Checkbox />} required label={<Trans i18nKey="signUpCreator.termsAndConditions" components={{ 
					termsOfServiceLink: <Link component={RouterLink} to={t('common.termsOfServiceHref')} target="_blank" />,
					privacyPolicyLink: <Link component={RouterLink} to={t('common.privacyPolicyHref')} target="_blank" />
				}} />} />
			</Box>
			<Box textAlign='center' py={4}>
				<Button variant='contained' size='large' type='submit' color='primary'>{t('signUpCreator.getStarted')}</Button>
			</Box>
			{errorMessage && <Typography variant='body1' textAlign='center' color='error'>{errorMessage}</Typography>}
		</form>
		<Box py={4} textAlign='center'>
			<Button onClick={() => logout()}>Log out</Button>
		</Box>
	</Box></SetupAccountLayout>;
};

export default SetupAccountCreator;